import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import { useExclusiveDrawer } from "../../drawerCoordinator";
import {
  PatientPeekContext,
  type PatientPeekTarget,
} from "./PatientPeekContext";

// The peek (and the amCharts it draws) loads on the first name clicked.
const PatientSidePeek = lazy(() => import("./PatientSidePeek"));

interface PatientPeekState {
  target: PatientPeekTarget | null;
  /** True once any patient was opened (gates loading the peek's chunk). */
  used: boolean;
  close: () => void;
}

const PatientPeekStateContext = createContext<PatientPeekState | null>(null);

/** Holds which patient the shell's side peek shows. */
export const PatientPeekProvider = ({ children }: PropsWithChildren) => {
  const [target, setTarget] = useState<PatientPeekTarget | null>(null);
  const [used, setUsed] = useState(false);
  const openPatient = useCallback((next: PatientPeekTarget) => {
    setUsed(true);
    setTarget(next);
  }, []);
  const close = useCallback(() => setTarget(null), []);
  // Opening another drawer closes this one, and vice versa.
  useExclusiveDrawer(!!target, close);

  const value = useMemo(() => ({ openPatient }), [openPatient]);
  const state = useMemo(() => ({ target, used, close }), [target, used, close]);

  return (
    <PatientPeekContext.Provider value={value}>
      <PatientPeekStateContext.Provider value={state}>
        {children}
      </PatientPeekStateContext.Provider>
    </PatientPeekContext.Provider>
  );
};

/**
 * The docked patient peek. Place it in the shell's flex row (beside the Ask
 * Circadia AI panel) so the page narrows instead of being covered.
 */
export const PatientPeekPanel = () => {
  const state = useContext(PatientPeekStateContext);
  if (!state?.used) return null;
  return (
    <Suspense fallback={null}>
      <PatientSidePeek target={state.target} onClose={state.close} />
    </Suspense>
  );
};
